// Select.tsx
"use client";

import { c as _c } from "react/compiler-runtime";
import { Select as HeroSelect, SelectItem as HeroSelectItem, SelectSection as HeroSelectSection } from "@heroui/react";
import type React from "react";
import { cn } from "../../lib/utils";
import selectStyles from "./Select.module.css";

// Get the original component props types
export type SelectProps = React.ComponentProps<typeof HeroSelect> & {
  className?: string;
};
export type SelectItemProps = React.ComponentProps<typeof HeroSelectItem> & {
  className?: string;
};
export type SelectSectionProps = React.ComponentProps<typeof HeroSelectSection> & {
  className?: string;
};

// Create the wrapper components
const SelectWrapper = t0 => {
  const $ = _c(23);
  let children;
  let className;
  let classNames;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
  }
  const {
    variant
  } = props;
  let t1;
  if ($[5] !== className) {
    t1 = cn(className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  const t2 = classNames?.value;
  let t3;
  if ($[7] !== t2) {
    t3 = cn("text-text-interactive-neutral-faint", t2);
    $[7] = t2;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const t4 = classNames?.trigger;
  let t5;
  if ($[9] !== t4) {
    t5 = cn("rounded-lg border-border-interactive-neutral-default text-neutral-onMatch-muted shadow-none hover:border-border-interactive-neutral-hover!", t4);
    $[9] = t4;
    $[10] = t5;
  } else {
    t5 = $[10];
  }
  let t6;
  if ($[11] !== classNames || $[12] !== t1 || $[13] !== t3 || $[14] !== t5) {
    t6 = {
      base: t1,
      value: t3,
      trigger: t5,
      ...classNames
    };
    $[11] = classNames;
    $[12] = t1;
    $[13] = t3;
    $[14] = t5;
    $[15] = t6;
  } else {
    t6 = $[15];
  }
  let t7;
  if ($[16] !== children || $[17] !== props || $[18] !== t6) {
    t7 = <HeroSelect radius="sm" {...props} classNames={t6}>{children}</HeroSelect>;
    $[16] = children;
    $[17] = props;
    $[18] = t6;
    $[19] = t7;
  } else {
    t7 = $[19];
  }
  let t8;
  if ($[20] !== t7 || $[21] !== variant) {
    t8 = <div className={selectStyles.select} data-variant={variant}>{t7}</div>;
    $[20] = t7;
    $[21] = variant;
    $[22] = t8;
  } else {
    t8 = $[22];
  }
  return t8;
};
const SelectItemWrapper = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let classNames;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn(className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== classNames || $[8] !== t1) {
    t2 = {
      base: t1,
      ...classNames
    };
    $[7] = classNames;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== props || $[12] !== t2) {
    t3 = <HeroSelectItem {...props} classNames={t2}>{children}</HeroSelectItem>;
    $[10] = children;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};
const SelectSectionWrapper = t0 => {
  const $ = _c(14);
  let children;
  let className;
  let classNames;
  let props;
  if ($[0] !== t0) {
    ({
      className,
      classNames,
      children,
      ...props
    } = t0);
    $[0] = t0;
    $[1] = children;
    $[2] = className;
    $[3] = classNames;
    $[4] = props;
  } else {
    children = $[1];
    className = $[2];
    classNames = $[3];
    props = $[4];
  }
  let t1;
  if ($[5] !== className) {
    t1 = cn(className);
    $[5] = className;
    $[6] = t1;
  } else {
    t1 = $[6];
  }
  let t2;
  if ($[7] !== classNames || $[8] !== t1) {
    t2 = {
      base: t1,
      ...classNames
    };
    $[7] = classNames;
    $[8] = t1;
    $[9] = t2;
  } else {
    t2 = $[9];
  }
  let t3;
  if ($[10] !== children || $[11] !== props || $[12] !== t2) {
    t3 = <HeroSelectSection {...props} classNames={t2}>{children}</HeroSelectSection>;
    $[10] = children;
    $[11] = props;
    $[12] = t2;
    $[13] = t3;
  } else {
    t3 = $[13];
  }
  return t3;
};

// Copy all static properties from the original components
Object.setPrototypeOf(SelectWrapper, HeroSelect);
Object.setPrototypeOf(SelectItemWrapper, HeroSelectItem);
Object.setPrototypeOf(SelectSectionWrapper, HeroSelectSection);

// Copy static properties that HeroUI's collection system might need
Object.assign(SelectWrapper, HeroSelect);
Object.assign(SelectItemWrapper, HeroSelectItem);
Object.assign(SelectSectionWrapper, HeroSelectSection);

// Ensure the components have the right displayName and type
SelectWrapper.displayName = (HeroSelect as React.ComponentType<unknown>).displayName || "Select";
SelectItemWrapper.displayName = (HeroSelectItem as React.ComponentType<unknown>).displayName || "SelectItem";
SelectSectionWrapper.displayName = (HeroSelectSection as React.ComponentType<unknown>).displayName || "SelectSection";

// Copy the $$typeof symbol if it exists (React uses this for component identification)
if ("$$typeof" in HeroSelect) {
  (SelectWrapper as unknown as Record<string, unknown>).$$typeof = (HeroSelect as unknown as Record<string, unknown>).$$typeof;
}
if ("$$typeof" in HeroSelectItem) {
  (SelectItemWrapper as unknown as Record<string, unknown>).$$typeof = (HeroSelectItem as unknown as Record<string, unknown>).$$typeof;
}
if ("$$typeof" in HeroSelectSection) {
  (SelectSectionWrapper as unknown as Record<string, unknown>).$$typeof = (HeroSelectSection as unknown as Record<string, unknown>).$$typeof;
}

// Export the wrapped components
export const Select = SelectWrapper;
export const SelectItem = SelectItemWrapper;
export const SelectSection = SelectSectionWrapper;